import React, { useContext, useState } from 'react';

import { DeactivateBasePlanLaunchDatesInput } from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';

import { contractConfigurationClient } from 'src/api/client';
import { ConfirmationModal } from 'src/components/ConfirmationModal';
import { DEFAULT_TOASTER_TIMEOUT } from 'src/constants';
import { AppContext } from 'src/contexts/appContext';
import { IBasePlanLaunchDateRowData } from 'src/types/IBasePlanLaunchDate';
import { IToast } from 'src/types/IToaster';
import { convertTimestampToYYYYMMDDInUTCTimezone } from 'src/util/dateTimeUtil';

interface ICancelBasePlanLaunchDateModalProps {
    basePlanLaunchDate?: IBasePlanLaunchDateRowData;
    isOpen: boolean;
    onClose: (refreshData?: boolean) => void;
}

export const CancelBasePlanLaunchDateModal = ({
    basePlanLaunchDate,
    isOpen,
    onClose
}: ICancelBasePlanLaunchDateModalProps) => {
    const [comments, setComments] = useState('');
    const { userInfo, setToasts, setTopAlertMsg } = useContext(AppContext);
    const widths = ['30%', '70%'];

    const cancelBasePlanLaunchDate = () => {
        if (!basePlanLaunchDate) return;

        const input = DeactivateBasePlanLaunchDatesInput.clone({
            basePlanLaunchDateIds: [basePlanLaunchDate.basePlanLaunchDateId],
            changedBy: userInfo.username
        });

        contractConfigurationClient.deactivateBasePlanLaunchDates(input).subscribe(
            () => {
                const toast: IToast = {
                    id: 'base-plan-launch-date-canceled-toast',
                    timeout: DEFAULT_TOASTER_TIMEOUT,
                    message: 'Base plan launch date canceled'
                };
                setToasts([toast]);
                setComments('');
                onClose(true);
            },
            (error: UnknownCoralError) => {
                // eslint-disable-next-line no-console
                console.error(error.receivedException.message);
                setTopAlertMsg(error.receivedException.message);
            }
        );
    };

    const handleOnClose = () => {
        setComments('');
        onClose();
    };

    return (
        <ConfirmationModal
            title="Cancel base plan launch date?"
            isOpen={isOpen}
            onClose={handleOnClose}
            comments={comments}
            onCommentsChange={(value) => setComments(value)}
            showComments={true}
            primaryButtonText="Yes, cancel it"
            primaryButtonOnClick={cancelBasePlanLaunchDate}
            secondaryButtonText="No, keep it"
            secondaryButtonOnClick={handleOnClose}
        >
            <Column>
                <Text>Are you sure you want to cancel this base plan launch date?</Text>
                <Column spacing="200">
                    <Row widths={widths}>
                        <Text type="h100">Base plan ID</Text>
                        <Text>{basePlanLaunchDate?.basePlanName}</Text>
                    </Row>
                    <Row widths={widths}>
                        <Text type="h100">Territory</Text>
                        <Text>{basePlanLaunchDate?.territoryCode}</Text>
                    </Row>
                    <Row widths={widths}>
                        <Text type="h100">Start date</Text>
                        <Text>
                            {basePlanLaunchDate?.startDate
                                ? convertTimestampToYYYYMMDDInUTCTimezone(new Date(basePlanLaunchDate.startDate))
                                : '--'}
                        </Text>
                    </Row>
                    <Row widths={widths}>
                        <Text type="h100">End date</Text>
                        <Text>
                            {basePlanLaunchDate?.endDate
                                ? convertTimestampToYYYYMMDDInUTCTimezone(new Date(basePlanLaunchDate.endDate))
                                : '--'}
                        </Text>
                    </Row>
                </Column>
            </Column>
        </ConfirmationModal>
    );
};
